import type { AdminCompanyFormManager } from '@/state/useAdminCompanyForm';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Field, FIELD_CONTROL, PillTabs } from '../../Dashboard/ConsolePanel';
import { PlanChoiceFields } from './PlanChoiceFields';

const TYPE_OPTIONS = [
  { label: 'Organization', value: 'Client Account' },
  { label: 'Agency', value: 'Agency' },
] as const;

const MODE_OPTIONS = [
  { label: 'Invoice', value: 'invoice' },
  { label: 'Free', value: 'free' },
] as const;

type CreateCompanyDialogProps = { form: AdminCompanyFormManager };

/**
 * A company billed outside Polar, with its first Owner invited by email. Signup only makes card
 * companies and a team invite cannot make an Owner, so an invoice or free customer starts here.
 * An agency gets its fee and chooses a plan for each client as it adds them; an organization
 * starts on the plan chosen here.
 */
export const CreateCompanyDialog = ({ form }: CreateCompanyDialogProps) => {
  const { form: values, setField } = form;
  const isAgency = values.type === 'Agency';

  return (
    <ConfirmDialog
      open={form.isOpen}
      title="Create company"
      description="Billed outside Polar. The owner gets an invitation email and sets a password to start."
      confirmLabel="Create and invite"
      disabled={!!form.blockingReason}
      disabledReason={form.blockingReason}
      onConfirm={form.submit}
      onCancel={form.close}
    >
      <div className="grid gap-4">
        <Field label="Company name" htmlFor="admin-company-name">
          <Input
            id="admin-company-name"
            value={values.name}
            onChange={(ev) => setField('name', ev.target.value)}
            placeholder="e.g. Acme Agency"
            className={FIELD_CONTROL}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Type"
            hint={isAgency ? 'Pays a fee; each client gets a plan when added.' : 'A single account on one plan.'}
          >
            <div className="whitespace-nowrap">
              <PillTabs
                options={TYPE_OPTIONS}
                value={values.type}
                onChange={(value) => setField('type', value === 'Agency' ? 'Agency' : 'Client Account')}
              />
            </div>
          </Field>
          <Field
            label="Billed by"
            hint={values.billingMode === 'free' ? 'Nobody is charged.' : 'Invoiced from the usage report.'}
          >
            <div className="whitespace-nowrap">
              <PillTabs
                options={MODE_OPTIONS}
                value={values.billingMode}
                onChange={(value) => setField('billingMode', value === 'free' ? 'free' : 'invoice')}
              />
            </div>
          </Field>
        </div>

        {!isAgency && (
          <PlanChoiceFields
            plan={values.plan}
            interval={values.interval}
            onPlanChange={(plan) => setField('plan', plan)}
            onIntervalChange={(interval) => setField('interval', interval)}
            hint="What its sessions are measured against. It can be changed here later."
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Owner first name" htmlFor="admin-owner-first-name">
            <Input
              id="admin-owner-first-name"
              value={values.firstName}
              onChange={(ev) => setField('firstName', ev.target.value)}
              autoComplete="off"
              className={FIELD_CONTROL}
            />
          </Field>
          <Field label="Owner last name" htmlFor="admin-owner-last-name">
            <Input
              id="admin-owner-last-name"
              value={values.lastName}
              onChange={(ev) => setField('lastName', ev.target.value)}
              autoComplete="off"
              className={FIELD_CONTROL}
            />
          </Field>
        </div>
        <Field label="Owner email" htmlFor="admin-owner-email" hint="The invitation goes here and works for 3 days.">
          <Input
            id="admin-owner-email"
            type="email"
            value={values.email}
            onChange={(ev) => setField('email', ev.target.value)}
            autoComplete="off"
            className={FIELD_CONTROL}
          />
        </Field>
      </div>
    </ConfirmDialog>
  );
};
